import { useMemo, useState } from "react";
import { Loader, Center } from "@mantine/core";
import { IconFileOff } from "@tabler/icons-react";
import {
  useAllInvoicesForArchiveQuery,
  ArchivePayment,
} from "../../api/queries";
import {
  YearFolder,
  countItemsInYear,
  groupByYearAndMonth,
  sortedYears as sortYearsDesc,
} from "../archiveTree";
import { InvoiceItem } from "./InvoiceItem";
import { InvoiceArchiveFilters } from "./InvoiceArchiveFilters";

const INVOICE_NOUN = { singular: "invoice", plural: "invoices" };

const getInvoiceDate = (invoice: ArchivePayment) =>
  invoice.date_created_in_accounting_software;

export const InvoiceArchive = () => {
  const { data: invoices, isLoading, error } = useAllInvoicesForArchiveQuery();
  const [expandedYears, setExpandedYears] = useState<Set<number>>(new Set());
  const [invoiceIdSearch, setInvoiceIdSearch] = useState<string>("");
  const [customerSearch, setCustomerSearch] = useState<string>("");

  const customerOptions = useMemo(() => {
    if (!invoices) return [];
    const uniqueCustomers = new Set<string>();
    invoices.forEach((invoice) => {
      if (invoice.customer_organization_name) {
        uniqueCustomers.add(invoice.customer_organization_name);
      }
    });
    return Array.from(uniqueCustomers).sort();
  }, [invoices]);

  const filteredInvoices = useMemo(() => {
    if (!invoices) return [];

    return invoices.filter((invoice) => {
      // Filter by invoice ID (external_id or id)
      if (invoiceIdSearch.trim()) {
        const searchLower = invoiceIdSearch.toLowerCase().trim();
        const matchesExternalId = invoice.external_id
          .toLowerCase()
          .includes(searchLower);
        const matchesId = invoice.id.toString().includes(searchLower);
        if (!matchesExternalId && !matchesId) {
          return false;
        }
      }

      // Filter by customer name (exact match)
      if (customerSearch) {
        if (invoice.customer_organization_name !== customerSearch) {
          return false;
        }
      }

      return true;
    });
  }, [invoices, invoiceIdSearch, customerSearch]);

  const groupedInvoices = useMemo(() => {
    if (!filteredInvoices.length) return {};
    return groupByYearAndMonth(filteredInvoices, getInvoiceDate);
  }, [filteredInvoices]);

  const sortedYears = useMemo(
    () => sortYearsDesc(groupedInvoices),
    [groupedInvoices],
  );

  const toggleYear = (year: number) => {
    setExpandedYears((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(year)) {
        newSet.delete(year);
      } else {
        newSet.add(year);
      }
      return newSet;
    });
  };

  const handleClearFilters = () => {
    setInvoiceIdSearch("");
    setCustomerSearch("");
  };

  if (isLoading) {
    return (
      <Center className="h-full pb-24">
        <Loader size="lg" />
      </Center>
    );
  }

  if (error) {
    return (
      <Center className="h-full pb-24">
        <p className="text-red-600">Failed to load invoices.</p>
      </Center>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6 rounded-xl border border-slate-200 bg-linear-to-br from-slate-50 to-slate-100 p-6 shadow-lg">
      <InvoiceArchiveFilters
        invoiceIdSearch={invoiceIdSearch}
        customerSearch={customerSearch}
        customerOptions={customerOptions}
        onInvoiceIdSearchChange={setInvoiceIdSearch}
        onCustomerSearchChange={setCustomerSearch}
        onClearFilters={handleClearFilters}
      />

      {sortedYears.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-lg border border-gray-200 bg-white py-16">
          <IconFileOff size={48} className="mb-4 text-gray-400" />
          <p className="text-lg text-gray-500">No invoices found</p>
          <p className="mt-1 text-sm text-gray-400">
            {invoiceIdSearch || customerSearch
              ? "No invoices match your filters. Try adjusting your search criteria."
              : "Invoices will appear here once they are created"}
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          {sortedYears.map((year) => (
            <YearFolder
              key={year}
              year={year}
              monthData={groupedInvoices[year]}
              itemCount={countItemsInYear(groupedInvoices, year)}
              isExpanded={expandedYears.has(year)}
              onToggle={() => toggleYear(year)}
              getDate={getInvoiceDate}
              getItemKey={(invoice) => invoice.id}
              renderItem={(invoice) => <InvoiceItem invoice={invoice} />}
              itemNoun={INVOICE_NOUN}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default InvoiceArchive;
